import type { SubPageContent } from "@/components/common/SubPageLayout";
import type { Lang } from "@/lib/i18n";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "FoodProcessingFiltration",
    relatedTopic: "food",
    hero: {
      categoryBadge: "Food & Pharmaceutical Filtration",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Stainless Steel Filter Mesh for Food Processing Applications",
      lead: "Engineered from AISI 316L and 304 stainless steel woven mesh to withstand frequent clean-in-place (CIP/SIP) washdowns, sanitary steam cycles, and continuous solid-liquid separation without particle shedding.",
      secondaryCta: {
        label: "Food & Pharmaceutical Filtration",
        to: "/FoodAndPharmaceuticalFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sanitary-st-8ec1d3.png",
        alt: "Sanitary stainless steel wire mesh filter basket and housing inside a food processing line",
      },
      cardNote: "Sanitary crevice-free welds & ultrasonic cleaning",
      cardLink: {
        label: "Request Technical Review",
        to: "/ContactPage",
      },
      specs: [
        { label: "Aperture Range", value: "5 µm – 500 µm" },
        { label: "Alloy Metallurgy", value: "AISI 316L / 304" },
        { label: "Fabrication", value: "Ultrasonic Cleaned / Passivated" },
        { label: "Sanitation Protocol", value: "CIP / SIP Steam Ready" },
      ],
    },
    overview: {
      eyebrow: "Process Engineering",
      heading: "Hygienic Solid-Liquid Straining & Particle Classification for Food Lines",
      paragraphs: [
        "In modern food and beverage production, filtration media must ensure reliable particle retention while maintaining strict sanitary integrity across prolonged processing campaigns. Product streams such as sugar syrups, edible oils, dairy mixtures, brewing mashes, and liquid flavorings carry viscous liquids and delicate particulates that require stable separation without product entrapment or media fatigue.",
        "MutualFilter provides sanitary stainless steel separation media manufactured from high-purity AISI 316L and 304 alloys. Precision plain weaves, twilled Dutch cloth, and robust wire screen assemblies are woven under tight dimensional tolerances to yield smooth, crevice-free pore geometries that facilitate free fluid discharge and prevent bacterial harborage.",
        "Every sanitary mesh element is ultrasonically degreased and chemically passivated to eliminate residual drawing lubricants and surface iron contamination. This structural stability ensures complete compatibility with repetitive clean-in-place (CIP) caustic washings, acid rinses, and steam-in-place (SIP) sanitation without wire displacement or media shedding.",
      ],
      keyPoints: [
        "Crevice-free hygienic weaves preventing bacterial entrapment and batch cross-contamination",
        "High tensile and hydraulic shock resistance under pulsed fluid pressure and repeated washdown cycles",
        "Precision apertures from 5 µm to 500 µm for sugar syrup clarification, dairy straining, and grain screening",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sanitary-st-8ec1d3.png",
        alt: "Sanitary stainless steel wire mesh filter basket and housing inside a food processing line",
      },
    },
    specTable: {
      heading: "Screen Media Configurations for Food & Beverage Processing",
      intro: "Representative metallic filter configurations engineered for liquid clarification, slurry straining, sanitary classification, and line security.",
      columns: [
        { key: "application", label: "Food Processing Application", align: "left" },
        { key: "mediaType", label: "Media Construction", align: "left" },
        { key: "aperture", label: "Nominal Aperture / Rating", align: "right" },
        { key: "alloy", label: "Standard Alloy", align: "right" },
        { key: "cleaning", label: "Cleaning Compatibility", align: "right" },
      ],
      rows: [
        {
          application: "Syrup & Molasses Clarification",
          mediaType: "Plain Dutch weave cloth / sintered laminate",
          aperture: "10 µm – 50 µm",
          alloy: "AISI 316L",
          cleaning: "Hot water rinse / CIP caustic",
        },
        {
          application: "Edible Oil Refining & Polishing",
          mediaType: "Twilled Dutch weave leaf screen",
          aperture: "5 µm – 25 µm",
          alloy: "AISI 316L",
          cleaning: "Solvent backwash / steam blast",
        },
        {
          application: "Dairy & Beverage Straining",
          mediaType: "Sanitary woven wire cylinder / basket",
          aperture: "50 µm – 250 µm",
          alloy: "AISI 316L / 304",
          cleaning: "Automated CIP spray / acid cycle",
        },
        {
          application: "Grain, Flour & Starch Screening",
          mediaType: "High-tensile square weave mesh",
          aperture: "150 µm – 500 µm",
          alloy: "AISI 304 / 316L",
          cleaning: "Vibratory cleaning / air purge",
        },
      ],
      caption: "All mesh dimensions, disc diameters, and cylindrical basket geometries are fabricated to custom equipment specifications.",
    },
    features: {
      heading: "Key Operational & Manufacturing Advantages",
      intro: "Our stainless steel food processing filter media combine sanitary metallurgical purity, non-shedding geometry, and mechanical endurance.",
      items: [
        {
          title: "Hygienic Crevice-Free Weaves",
          text: "Uniform wire crossings and smooth surface topographies prevent particulate entrapment, microbial growth pockets, and cross-batch contamination.",
        },
        {
          title: "Zero Media Migration",
          text: "Solid metallic wire structures and diffusion-bonded layers eliminate fiber shedding or uncoiling into sensitive food or beverage products.",
        },
        {
          title: "CIP & SIP Cleanability",
          text: "Passivated stainless steel alloys endure cyclic exposure to hot caustic sanitizers, nitric acid rinses, and high-temperature steam sterilization.",
        },
        {
          title: "Ultrasonic Degreasing",
          text: "Components undergo multi-stage ultrasonic degreasing to remove drawing oils and manufacturing residues prior to packaging and dispatch.",
        },
      ],
    },
    related: {
      heading: "Related Sanitary Applications & Recommended Products",
      intro: "Explore complementary food and pharmaceutical separation applications alongside precision metallic filter media.",
      cards: [
        {
          title: "Food & Pharmaceutical Filtration",
          text: "Hygienic stainless steel mesh solutions engineered for food-contact fluid straining, powder separation, and pharmaceutical purity.",
          to: "/FoodAndPharmaceuticalFiltrationPage",
          badge: "Parent Application",
          linkLabel: "View Application Overview",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-photograph-of-a-polished-stainless-70af93.png",
            alt: "Hygienic stainless steel wire mesh and filter disc components for food and pharmaceutical processing",
          },
        },
        {
          title: "Pharmaceutical Filtration",
          text: "Pore-stable sintered laminates and precision woven mesh for active pharmaceutical ingredients, solvent recovery, and sterile fluids.",
          to: "/PharmaceuticalFiltrationPage",
          badge: "Sibling Application",
          linkLabel: "Explore Pharmaceutical Filtration",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-pharmaceuti-9d7134.png",
            alt: "Precision sintered stainless steel multilayer filter element for pharmaceutical processing",
          },
        },
        {
          title: "Woven Wire Mesh",
          text: "Square weave and Dutch weave stainless steel wire cloth providing calibrated apertures for liquid clarification and powder sifting.",
          to: "/WovenWireMeshPage",
          badge: "Recommended Product",
          linkLabel: "Explore Woven Wire Mesh",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Plain Dutch weave stainless steel wire cloth for sanitary liquid clarification",
          },
        },
        {
          title: "Filter Components",
          text: "Custom fabricated sanitary filter baskets, cylinders, stamped discs, and screen packs for food and beverage production equipment.",
          to: "/FilterComponentsPage",
          badge: "Recommended Product",
          linkLabel: "Explore Filter Components",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/pharma_sterile_gas_filter.png",
            alt: "All-metal sanitary filter cartridge for steam and liquid filtration",
          },
        },
      ],
    },
    cta: {
      id: "food-processing-quote",
      eyebrow: "Engineering Consultation",
      heading: "Request a Custom Food Processing Filter Mesh Specification",
      lead: "Provide your required micron rating, equipment dimensions, alloy selection, and operating cleaning protocol for an engineering review and quotation.",
      secondaryLabel: "Food & Pharmaceutical Filtration",
      secondaryTo: "/FoodAndPharmaceuticalFiltrationPage",
      note: "Material test reports (MTR) and positive material identification (PMI) available when specified in the order.",
    },
  },
  zh: {
    breadcrumbKey: "FoodProcessingFiltration",
    relatedTopic: "food",
    hero: {
      categoryBadge: "食品与制药过滤",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "食品加工应用卫生级不锈钢过滤丝网",
      lead: "精选 AISI 316L 与 304 不锈钢编织丝网与密纹滤材，专为食品饮料与乳品加工的高频在线清洗 (CIP/SIP)、卫生级蒸汽灭菌及连续固液分离设计，杜绝颗粒与纤维脱落。",
      secondaryCta: {
        label: "食品与制药过滤总览",
        to: "/FoodAndPharmaceuticalFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sanitary-st-8ec1d3.png",
        alt: "食品加工生产线卫生级不锈钢丝网滤篮与过滤器壳体",
      },
      cardNote: "卫生级无死角焊接与超声波深度脱脂",
      cardLink: {
        label: "技术方案咨询",
        to: "/ContactPage",
      },
      specs: [
        { label: "截留孔径范围", value: "5 µm – 500 µm" },
        { label: "常用材质牌号", value: "AISI 316L / 304" },
        { label: "表面洁净处理", value: "超声波除油脱脂 / 化学钝化" },
        { label: "卫生清洗规范", value: "兼容 CIP / SIP 蒸汽清洗" },
      ],
    },
    overview: {
      eyebrow: "工艺工程解析",
      heading: "针对食品加工产线的高洁净固液过滤与颗粒分级",
      paragraphs: [
        "在现代食品与饮料工业生产中，过滤介质不仅需要确保精准可重复的颗粒拦截率，还必须在长时间连续生产中维持严苛的卫生完整性。糖浆、食用油、乳品流体、酿造麦汁和液体香精等物料具有较高粘度或细微悬浮颗粒，需要流道顺畅且不易滞留残渣的金属筛滤构件。",
        "MutualFilter 提供以高纯度 AISI 316L 与 304 不锈钢精工编织的卫生级分离滤材。包括精密平纹编织网、斜纹荷兰编织滤布以及刚性滤网构件，均在严格公差控制下织造，形成平滑、无缝隙死角的微孔型面，有助于流体快速通流并防止细菌微生物滋生。",
        "所有卫生级过滤网构件均经过多道超声波脱脂与表面酸洗钝化处理，彻底去除微量拔丝润滑油与游离铁污染。滤材具备出色的结构稳定性，能够全面耐受定期的在线热碱 CIP 清洗、酸洗消毒与 SIP 高温高压蒸汽灭菌，杜绝金属丝移位或介质脱落风险。",
      ],
      keyPoints: [
        "平滑抗滞留卫生级编织结构，杜绝死角物料残留与批次交叉污染",
        "优异抗拉强度与经纬咬合力，耐受脉冲流体压力、水锤冲击与高频清洗",
        "5 µm 至 500 µm 精准孔径控制，适用于糖浆澄清、乳品过滤与谷物干燥筛分",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sanitary-st-8ec1d3.png",
        alt: "食品加工生产线卫生级不锈钢丝网滤篮与过滤器壳体",
      },
    },
    specTable: {
      heading: "食品与饮料加工常用金属滤材配置参数",
      intro: "以下列出适用于液体澄清、浆料粗滤、卫生级筛分与管线安检的典型金属过滤介质配置。",
      columns: [
        { key: "application", label: "食品加工应用环节", align: "left" },
        { key: "mediaType", label: "核心构型与介质", align: "left" },
        { key: "aperture", label: "名义截留孔径 / 精度", align: "right" },
        { key: "alloy", label: "推荐合金材质", align: "right" },
        { key: "cleaning", label: "清洗再生方式", align: "right" },
      ],
      rows: [
        {
          application: "糖浆与糖蜜精密澄清",
          mediaType: "平纹荷兰编织滤布 / 烧结复合网板",
          aperture: "10 µm – 50 µm",
          alloy: "AISI 316L",
          cleaning: "热水冲洗 / CIP 碱洗循环",
        },
        {
          application: "食用油精炼与叶滤机精滤",
          mediaType: "斜纹荷兰密纹网叶滤网片",
          aperture: "5 µm – 25 µm",
          alloy: "AISI 316L",
          cleaning: "溶剂反冲 / 蒸汽吹扫",
        },
        {
          application: "乳品与饮料管线除杂",
          mediaType: "卫生级编织丝网圆筒 / 滤篮",
          aperture: "50 µm – 250 µm",
          alloy: "AISI 316L / 304",
          cleaning: "自动化 CIP 喷淋 / 酸洗循环",
        },
        {
          application: "谷物、面粉与淀粉振动筛分",
          mediaType: "高张力方孔编织不锈钢丝网",
          aperture: "150 µm – 500 µm",
          alloy: "AISI 304 / 316L",
          cleaning: "机械振动清理 / 压缩空气吹扫",
        },
      ],
      caption: "各项丝网尺寸、圆形滤片直径及滤筒结构均可根据客户机械设备图纸定制加工。",
    },
    features: {
      heading: "关键运行与制造技术优势",
      intro: "我们的食品加工不锈钢过滤介质兼具材料纯净度、零介质脱落特征与可靠的机械耐用性。",
      items: [
        {
          title: "卫生级抗死角编织型面",
          text: "均匀的经纬交织节点与平整表面有效防止微粒嵌塞，消除微生物滋生死角，杜绝批次间的物料交叉污染。",
        },
        {
          title: "零介质脱落与高结构完整性",
          text: "全金属刚性丝线构造与扩散粘结烧结复合层，杜绝任何纤维脱落或网丝松动进入食品饮料流体。",
        },
        {
          title: "CIP 与 SIP 灭菌清洗耐受",
          text: "经过钝化处理的不锈钢材质能够耐受周期性高温热碱液清洗、硝酸中和漂洗及高温灭菌蒸汽冲刷。",
        },
        {
          title: "多级超声波除油脱脂",
          text: "出厂前经过多级精密超声波清洗，彻底清除金属拔丝润滑剂与微量油脂残留，确保符合严格卫生规范。",
        },
      ],
    },
    related: {
      heading: "相关卫生级应用与推荐产品",
      intro: "浏览 MutualFilter 专为食品与制药行业打造的互补过滤方案及精密金属过滤介质。",
      cards: [
        {
          title: "食品与制药过滤",
          text: "涵盖食品接触流体精滤、粉体筛分与制药高纯工艺的全金属卫生级介质综合方案。",
          to: "/FoodAndPharmaceuticalFiltrationPage",
          badge: "父级应用分类",
          linkLabel: "查看应用总览",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-photograph-of-a-polished-stainless-70af93.png",
            alt: "用于食品与制药工艺的高精度卫生级不锈钢过滤丝网与滤片组件",
          },
        },
        {
          title: "制药工艺过滤",
          text: "孔径稳定扩散烧结复合网与精密荷兰织网，专为原料药提纯、母液回收与无菌流体过滤设计。",
          to: "/PharmaceuticalFiltrationPage",
          badge: "同级应用",
          linkLabel: "探索制药工艺过滤",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-pharmaceuti-9d7134.png",
            alt: "用于制药工艺的高精度多层不锈钢烧结滤芯结构",
          },
        },
        {
          title: "编织丝网",
          text: "方孔编织网与荷兰密纹不锈钢丝布，提供高精度校准微孔，适用于液体澄清与粉体分级。",
          to: "/WovenWireMeshPage",
          badge: "推荐产品",
          linkLabel: "探索编织丝网",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "用于卫生级液体澄清的平纹荷兰编织不锈钢丝布",
          },
        },
        {
          title: "过滤组件",
          text: "定制卫生级不锈钢滤筒、滤篮、冲压滤片及多层滤网包，适配各类食品饮料自动化过滤设备。",
          to: "/FilterComponentsPage",
          badge: "推荐产品",
          linkLabel: "探索过滤组件",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/pharma_sterile_gas_filter.png",
            alt: "用于蒸汽与卫生液体过滤的全金属滤芯",
          },
        },
      ],
    },
    cta: {
      id: "food-processing-quote",
      eyebrow: "定制方案咨询",
      heading: "获取食品加工过滤丝网与滤材技术报价",
      lead: "请提供您的目标截留精度、设备安装尺寸、不锈钢材质要求及清洗消毒流程，我们的工程团队将为您提供针对性的选型与报价方案。",
      secondaryLabel: "返回食品与制药过滤",
      secondaryTo: "/FoodAndPharmaceuticalFiltrationPage",
      note: "材质报告 (MTR) 与化学成分检测 (PMI) 可按订单要求提供。",
    },
  },
  es: {
    breadcrumbKey: "FoodProcessingFiltration",
    relatedTopic: "food",
    hero: {
      categoryBadge: "Filtración Alimentaria y Farmacéutica",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Malla de Alambre de Acero Inoxidable para Procesamiento de Alimentos",
      lead: "Fabricada en malla tejida de acero inoxidable AISI 316L y 304 para soportar ciclos frecuentes de limpieza in situ (CIP/SIP), vapor sanitario y separación continuo sin desprendimiento de partículas.",
      secondaryCta: {
        label: "Filtración Alimentaria y Farmacéutica",
        to: "/FoodAndPharmaceuticalFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sanitary-st-8ec1d3.png",
        alt: "Cesta de filtro de malla de acero inoxidable sanitario en línea de procesamiento de alimentos",
      },
      cardNote: "Soldaduras sanitarias sin ranuras y desengrasado ultrasónico",
      cardLink: {
        label: "Consulta Técnica",
        to: "/ContactPage",
      },
      specs: [
        { label: "Rango de Abertura", value: "5 µm – 500 µm" },
        { label: "Grados de Aleación", value: "AISI 316L / 304" },
        { label: "Tratamiento Superficial", value: "Limpieza Ultrasónica / Pasivado" },
        { label: "Protocolo Sanitario", value: "Compatible CIP / SIP Vapor" },
      ],
    },
    overview: {
      eyebrow: "Ingeniería de Procesos",
      heading: "Colado Higiénico de Sólidos-Líquidos y Clasificación en Líneas de Alimentos",
      paragraphs: [
        "En la producción moderna de alimentos y bebidas, los medios filtrantes deben garantizar una retención de partículas repetible y mantener una estricta integridad sanitaria en campañas prolongadas. Corrientes como jarabes de azúcar, aceites comestibles, mezclas lácteas y aromas líquidos exigen una separación fluida sin retención de producto ni fatiga del material.",
        "MutualFilter suministra medios de separación metálicos higiénicos fabricados con aleaciones de alta pureza AISI 316L y 304. Tejidos lisos de precisión, telas Dutch cruzadas y conjuntos de cribado se tejen con tolerancias rigurosas para crear poros lisos y sin ranuras que facilitan el flujo libre y previenen el desarrollo microbiano.",
        "Cada elemento de malla sanitaria pasa por desengrasado ultrasónico y pasivado químico para eliminar lubricantes residuales del trefilado e impurezas de hierro superficial. Esta estabilidad estructural garantiza total compatibilidad con lavados alcalinos CIP, enjuagues ácidos y vapor SIP sin desplazamiento de alambres ni desprendimiento de fibras.",
      ],
      keyPoints: [
        "Tejidos higiénicos sin ranuras que previenen retención bacteriana y contaminación cruzada entre lotes",
        "Alta resistencia a la tracción y al choque hidráulico bajo presiones pulsadas y ciclos continuos de lavado",
        "Aberturas precisas de 5 µm a 500 µm para clarificación de jarabes, filtración de lácteos y tamizado de granos",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sanitary-st-8ec1d3.png",
        alt: "Cesta de filtro de malla de acero inoxidable sanitario en línea de procesamiento de alimentos",
      },
    },
    specTable: {
      heading: "Configuraciones de Mallas para Procesamiento de Alimentos y Bebidas",
      intro: "Medios metálicos representativos para clarificación de líquidos, retención en suspensiones, cribado y seguridad de línea.",
      columns: [
        { key: "application", label: "Aplicación Alimentaria", align: "left" },
        { key: "mediaType", label: "Tipo de Medio Filtrante", align: "left" },
        { key: "aperture", label: "Abertura / Grado Nominal", align: "right" },
        { key: "alloy", label: "Aleación Estándar", align: "right" },
        { key: "cleaning", label: "Método de Limpieza", align: "right" },
      ],
      rows: [
        {
          application: "Clarificación de Jarabes y Melazas",
          mediaType: "Tejido Dutch liso / laminado sinterizado",
          aperture: "10 µm – 50 µm",
          alloy: "AISI 316L",
          cleaning: "Enjuague agua caliente / sosa CIP",
        },
        {
          application: "Refinado y Filtrado de Aceites Comestibles",
          mediaType: "Malla Dutch cruzada para filtros de hojas",
          aperture: "5 µm – 25 µm",
          alloy: "AISI 316L",
          cleaning: "Retrolavado con solvente / vapor",
        },
        {
          application: "Retención en Líneas Lácteas y Bebidas",
          mediaType: "Cilindro / cesta de malla tejida sanitaria",
          aperture: "50 µm – 250 µm",
          alloy: "AISI 316L / 304",
          cleaning: "Aspersión CIP automática / ciclo ácido",
        },
        {
          application: "Tamizado de Granos, Harina y Almidón",
          mediaType: "Malla tejida cuadrada de alta tensión",
          aperture: "150 µm – 500 µm",
          alloy: "AISI 304 / 316L",
          cleaning: "Limpieza por vibración / purga de aire",
        },
      ],
      caption: "Todas las dimensiones de malla, discos y cestas cilíndricas se fabrican a medida según planos del equipo.",
    },
    features: {
      heading: "Ventajas Operativas y de Fabricación",
      intro: "Nuestros medios filtrantes para procesamiento de alimentos combinan pureza metalúrgica, cero desprendimiento y durabilidad.",
      items: [
        {
          title: "Tejidos Higiénicos sin Ranuras",
          text: "Puntos de cruce uniformes y topografía superficial lisa que evitan acumulación de sólidos y contaminación entre lotes.",
        },
        {
          title: "Cero Migración de Material",
          text: "Estructuras de alambre metálico sólido y capas sinterizadas que evitan desprendimiento de fibras en fluidos sensibles.",
        },
        {
          title: "Limpiabilidad CIP y SIP",
          text: "Aleaciones de acero inoxidable pasivadas que soportan productos químicos cáusticos, enjuagues ácidos y vapor a presión.",
        },
        {
          title: "Desengrasado Ultrasónico",
          text: "Limpieza ultrasónica exhaustiva en múltiples etapas para eliminar aceites de fabricación antes del embalaje.",
        },
      ],
    },
    related: {
      heading: "Aplicaciones Sanitarias y Productos Relacionados",
      intro: "Explore soluciones complementarias para alimentos y farmacia junto a medios metálicos de precisión.",
      cards: [
        {
          title: "Filtración Alimentaria y Farmacéutica",
          text: "Soluciones completas de malla higiénica para colado de fluidos, tamizado de polvos y pureza de procesos.",
          to: "/FoodAndPharmaceuticalFiltrationPage",
          badge: "Aplicación Principal",
          linkLabel: "Ver Visión General",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-photograph-of-a-polished-stainless-70af93.png",
            alt: "Malla de alambre de acero inoxidable higiénica de alta precisión y discos filtrantes para procesos alimentarios y farmacéuticos",
          },
        },
        {
          title: "Filtración Farmacéutica",
          text: "Laminados sinterizados con porosidad estable y mallas tejidas para principios activos y fluidos estériles.",
          to: "/PharmaceuticalFiltrationPage",
          badge: "Aplicación Hermana",
          linkLabel: "Explorar Filtración Farmacéutica",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-pharmaceuti-9d7134.png",
            alt: "Elemento filtrante multicapa de acero inoxidable sinterizado de precisión para procesos farmacéuticos",
          },
        },
        {
          title: "Malla de Alambre Tejida",
          text: "Telas de alambre cuadradas y de tejido Dutch con aberturas calibradas para clarificación de líquidos y polvos.",
          to: "/WovenWireMeshPage",
          badge: "Producto Recomendado",
          linkLabel: "Explorar Malla Tejida",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Malla de alambre de tejido Dutch liso de acero inoxidable para clarificación sanitaria de líquidos",
          },
        },
        {
          title: "Componentes Filtrantes",
          text: "Cestas sanitarias, cilindros soldados, discos troquelados y paquetes de malla para líneas de alimentos y bebidas.",
          to: "/FilterComponentsPage",
          badge: "Producto Recomendado",
          linkLabel: "Explorar Componentes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/pharma_sterile_gas_filter.png",
            alt: "Cartucho filtrante sanitario metálico para vapor y líquidos",
          },
        },
      ],
    },
    cta: {
      id: "food-processing-quote",
      eyebrow: "Consulta Técnica",
      heading: "Solicite Cotización para Mallas de Procesamiento de Alimentos",
      lead: "Indique la clasificación en micras requerida, dimensiones del equipo, aleación y protocolo de limpieza para una revisión técnica y cotización.",
      secondaryLabel: "Volver a Filtración Alimentaria",
      secondaryTo: "/FoodAndPharmaceuticalFiltrationPage",
      note: "Informes de ensayo de material (MTR) y análisis PMI disponibles cuando se especifiquen en el pedido.",
    },
  },
  de: {
    breadcrumbKey: "FoodProcessingFiltration",
    relatedTopic: "food",
    hero: {
      categoryBadge: "Lebensmittel- & Pharmafiltration",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Edelstahl-Filtergewebe für die Lebensmittelverarbeitung",
      lead: "Gefertigt aus Edelstahl-Drahtgewebe AISI 316L und 304 für anspruchsvolle CIP/SIP-Reinigungszyklen, Heißdampf-Sterilisation und kontinuierliche Fest-Flüssig-Trennung ohne Partikelabrieb.",
      secondaryCta: {
        label: "Lebensmittel- & Pharmafiltration",
        to: "/FoodAndPharmaceuticalFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sanitary-st-8ec1d3.png",
        alt: "Sanitär-Edelstahldrahtgewebe-Filterkorb in einer Lebensmittelverarbeitungslinie",
      },
      cardNote: "Spaltfreie Schweißverbindungen & Ultraschallreinigung",
      cardLink: {
        label: "Technische Beratung",
        to: "/ContactPage",
      },
      specs: [
        { label: "Porenweitenbereich", value: "5 µm – 500 µm" },
        { label: "Werkstoffe", value: "AISI 316L / 304" },
        { label: "Oberflächenbehandlung", value: "Ultraschallgereinigt / Passiviert" },
        { label: "Hygieneprotokoll", value: "CIP / SIP Dampfbeständig" },
      ],
    },
    overview: {
      eyebrow: "Verfahrenstechnik",
      heading: "Hygienische Fest-Flüssig-Siebung & Partikelklassierung in Lebensmittelprozessen",
      paragraphs: [
        "In der modernen Lebensmittel- und Getränkeproduktion müssen Filtermedien eine reproduzierbare Partikelrückhaltung bei gleichzeitiger Einhaltung strenger Hygienestandards gewährleisten. Produktströme wie Zuckersirup, Speiseöle, Milcherzeugnisse, Braumaischen und flüssige Aromen erfordern eine zuverlässige Trennung ohne Produktanhaftungen.",
        "MutualFilter liefert hygienische metallische Trennmedien aus hochreinen Legierungen AISI 316L und 304. Präzise Glattgewebe, Köpertressen und formstabile Filterkörbe werden unter engen Toleranzen gewebt, um glatte, spaltfreie Porenstrukturen zu schaffen, die den ungehinderten Durchfluss fördern und Keimansammlungen verhindern.",
        "Jedes sanitäre Gewebeelement wird mehrstufig im Ultraschallbad entfettet und chemisch passiviert, um Ziehmittelreste und freie Eisenpartikel zu entfernen. Dies gewährleistet Beständigkeit gegen alkalische CIP-Spülungen, saure Reinigungsphasen und SIP-Dampfsterilisation ohne Drahtverschiebung oder Fasereintrag.",
      ],
      keyPoints: [
        "Spaltfreie hygienische Gewebestrukturen verhindern Bakterienablagerungen und Kreuzkontamination",
        "Hohe mechanische Festigkeit und Beständigkeit gegen Druckschläge bei intensiven Waschzyklen",
        "Präzise Porenweiten von 5 µm bis 500 µm für Sirupklärung, Milchfiltration und Getreidesiebung",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sanitary-st-8ec1d3.png",
        alt: "Sanitär-Edelstahldrahtgewebe-Filterkorb in einer Lebensmittelverarbeitungslinie",
      },
    },
    specTable: {
      heading: "Filtermedien-Konfigurationen für Lebensmittel- und Getränkeprozesse",
      intro: "Repräsentative Siebmedien für Flüssigkeitsklärung, Schlammfiltration, Schutzsiebung und hygienische Trennstufen.",
      columns: [
        { key: "application", label: "Lebensmittelprozess", align: "left" },
        { key: "mediaType", label: "Filtermedium-Aufbau", align: "left" },
        { key: "aperture", label: "Nennfeinheit / Porenweite", align: "right" },
        { key: "alloy", label: "Standard-Werkstoff", align: "right" },
        { key: "cleaning", label: "Reinigungsmethode", align: "right" },
      ],
      rows: [
        {
          application: "Sirup- & Melasseklärung",
          mediaType: "Glatte Tresse / Sinterlaminat",
          aperture: "10 µm – 50 µm",
          alloy: "AISI 316L",
          cleaning: "Heißwasser / CIP-Laugenzyklus",
        },
        {
          application: "Speiseölraffination & Blattfilter",
          mediaType: "Köpertresse als Siebblattbezug",
          aperture: "5 µm – 25 µm",
          alloy: "AISI 316L",
          cleaning: "Lösemittel-Rückspülung / Dampf",
        },
        {
          application: "Milch- & Getränkefiltration",
          mediaType: "Hygienischer Drahtgewebezylinder / Korb",
          aperture: "50 µm – 250 µm",
          alloy: "AISI 316L / 304",
          cleaning: "Automatische CIP-Düse / Säurephase",
        },
        {
          application: "Getreide-, Mehl- & Stärkesiebung",
          mediaType: "Hochfestes Quadratmaschen-Gewebe",
          aperture: "150 µm – 500 µm",
          alloy: "AISI 304 / 316L",
          cleaning: "Vibrationsabreinigung / Druckluft",
        },
      ],
      caption: "Alle Abmessungen, Ronden und zylindrischen Filterkörbe werden nach individuellen Kundenanforderungen gefertigt.",
    },
    features: {
      heading: "Betriebliche & Fertigungstechnische Vorteile",
      intro: "Unsere Edelstahlmedien für die Lebensmittelverarbeitung vereinen Werkstoffreinheit, Abriebfreiheit und hohe Dauerfestigkeit.",
      items: [
        {
          title: "Spaltfreie hygienische Gewebestruktur",
          text: "Gleichmäßige Kreuzungspunkte und glatte Oberflächen verhindern Feststoffansammlungen und mikrobielle Rückstände.",
        },
        {
          title: "Vollkommen partikelfrei",
          text: "Ganzmetall-Drahtkonstruktionen und Sinterverbunde schließen das Ausfasern von Medien in sensitive Produkte aus.",
        },
        {
          title: "CIP- und SIP-Reinigbarkeit",
          text: "Passivierte Edelstähle widerstehen wiederholten Einwirkungen von Heißlauge, Salpetersäure und Heißdampf.",
        },
        {
          title: "Ultraschall-Entfettung",
          text: "Mehrstufige Ultraschallreinigung beseitigt Ziehmittelreste zuverlässig vor der Endkontrolle und Verpackung.",
        },
      ],
    },
    related: {
      heading: "Verwandte Hygieneanwendungen & Empfohlene Produkte",
      intro: "Entdecken Sie verwandte Anwendungen in der Lebensmittel- und Pharmaindustrie sowie präzise Metallfiltermedien.",
      cards: [
        {
          title: "Lebensmittel- & Pharmafiltration",
          text: "Hygienische Edelstahl-Filterlösungen für Flüssigkeitssiebung, Pulverklassierung und anspruchsvolle Prozessreinheit.",
          to: "/FoodAndPharmaceuticalFiltrationPage",
          badge: "Hauptanwendung",
          linkLabel: "Übersicht ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-photograph-of-a-polished-stainless-70af93.png",
            alt: "Hochpräzises hygienisches Edelstahl-Drahtgewebe und Filterscheiben-Komponenten für Lebensmittel- und Pharmaprozesse",
          },
        },
        {
          title: "Pharmazeutische Filtration",
          text: "Porenstabile Sinterlaminate und Präzisionsgewebe für pharmazeutische Wirkstoffe und sterile Flüssigkeiten.",
          to: "/PharmaceuticalFiltrationPage",
          badge: "Geschwisteranwendung",
          linkLabel: "Pharmafiltration ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-pharmaceuti-9d7134.png",
            alt: "Präzisions-Edelstahl-Mehrlagenfilterelement für pharmazeutische Prozesse",
          },
        },
        {
          title: "Drahtgewebe",
          text: "Quadratmaschen und Tressengewebe aus Edelstahl mit kalibrierten Porenweiten für Flüssigkeits- und Pulversiebung.",
          to: "/WovenWireMeshPage",
          badge: "Empfohlenes Produkt",
          linkLabel: "Drahtgewebe entdecken",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Glatte Tresse Edelstahldrahtgewebe für sanitäre Flüssigkeitsklärung",
          },
        },
        {
          title: "Filterkomponenten",
          text: "Hygienische Filterkörbe, Zylinder, Stanzronden und Siebpakete für Lebensmittel- und Getränkeanlagen.",
          to: "/FilterComponentsPage",
          badge: "Empfohlenes Produkt",
          linkLabel: "Filterkomponenten entdecken",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/pharma_sterile_gas_filter.png",
            alt: "Ganzmetall-Sanitärfilterkerze für pharmazeutischen Dampf und Flüssigkeiten",
          },
        },
      ],
    },
    cta: {
      id: "food-processing-quote",
      eyebrow: "Technische Anfrage",
      heading: "Angebot für Filtergewebe in der Lebensmittelverarbeitung anfordern",
      lead: "Nennen Sie Filterfeinheit, Einbaumaße, Werkstoffanforderung und Reinigungsprotokoll für eine detaillierte technische Prüfung.",
      secondaryLabel: "Zurück zu Lebensmittel & Pharma",
      secondaryTo: "/FoodAndPharmaceuticalFiltrationPage",
      note: "Werksprüfzeugnisse (MTR) und PMI-Werkstoffanalysen auf Anfrage gemäß Auftragsvereinbarung verfügbar.",
    },
  },
  fr: {
    breadcrumbKey: "FoodProcessingFiltration",
    relatedTopic: "food",
    hero: {
      categoryBadge: "Filtration Agroalimentaire et Pharmaceutique",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Toile Métallique en Acier Inoxydable pour Procédés Agroalimentaires",
      lead: "Conçue à partir de toiles tissées en acier inoxydable AISI 316L et 304 pour résister aux lavages en place fréquents (NEP/SEP), aux cycles de vapeur et à la séparation continue sans relargage de particules.",
      secondaryCta: {
        label: "Filtration Agroalimentaire et Pharmaceutique",
        to: "/FoodAndPharmaceuticalFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sanitary-st-8ec1d3.png",
        alt: "Panier filtrant en toile inox sanitaire dans une ligne de production agroalimentaire",
      },
      cardNote: "Soudures sanitaires sans recoins et dégraissage ultrasons",
      cardLink: {
        label: "Consultation Technique",
        to: "/ContactPage",
      },
      specs: [
        { label: "Plage d'Ouverture", value: "5 µm – 500 µm" },
        { label: "Nuances d'Alliages", value: "AISI 316L / 304" },
        { label: "Traitement de Surface", value: "Nettoyage Ultrasons / Passivé" },
        { label: "Protocole Sanitaire", value: "Compatible Vapeur NEP / SEP" },
      ],
    },
    overview: {
      eyebrow: "Génie des Procédés",
      heading: "Tamisage et Filtration Sanitaire Solide-Liquide pour l'Agroalimentaire",
      paragraphs: [
        "Dans l'industrie agroalimentaire moderne, les médias filtrants doivent garantir une rétention de particules constante tout en maintenant une hygiène absolue au cours de campagnes de production prolongées. Les sirops de sucre, huiles alimentaires, produits laitiers, moûts de brasserie et arômes liquides nécessitent une séparation nette sans rétention résiduelle.",
        "MutualFilter propose des toiles métalliques de séparation sanitaire élaborées à partir d'alliages AISI 316L et 304 de haute pureté. Tissages unis de précision, toiles hollandaises sergées et paniers rigides sont confectionnés selon des tolérances serrées pour obtenir des mailles lisses évitant toute zone morte propice aux biofilms.",
        "Chaque élément filtrant est dégraissé aux ultrasons et passivé chimiquement pour éliminer les lubrifiants d'étirage et toute contamination ferreuse de surface. Cette structure assure une résistance complète aux nettoyages en place alcalins (NEP), rinçages acides et stérilisations à la vapeur sans déformation de la toile ni relargage de fibres.",
      ],
      keyPoints: [
        "Tissages hygiéniques sans recoins éliminant les zones mortes et les risques de contamination croisée",
        "Haute résistance à la traction et aux chocs hydrauliques sous pression pulsée répétée",
        "Ouvertures de précision de 5 µm à 500 µm pour clarification de sirops, produits laitiers et tamisage de grains",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sanitary-st-8ec1d3.png",
        alt: "Panier filtrant en toile inox sanitaire dans une ligne de production agroalimentaire",
      },
    },
    specTable: {
      heading: "Configurations de Toiles pour Procédés Agroalimentaires et Boissons",
      intro: "Médias filtrants métalliques types pour clarification de liquides, tamisage de pâtes, filtration de sécurité et séparation hygiénique.",
      columns: [
        { key: "application", label: "Application Agroalimentaire", align: "left" },
        { key: "mediaType", label: "Structure du Média", align: "left" },
        { key: "aperture", label: "Seuil / Ouverture Nominale", align: "right" },
        { key: "alloy", label: "Alliage Standard", align: "right" },
        { key: "cleaning", label: "Mode de Nettoyage", align: "right" },
      ],
      rows: [
        {
          application: "Clarification de Sirops et Mélasses",
          mediaType: "Tissage hollandais uni / stratifié fritté",
          aperture: "10 µm – 50 µm",
          alloy: "AISI 316L",
          cleaning: "Rinçage eau chaude / cycle NEP soude",
        },
        {
          application: "Raffinage d'Huiles et Filtres à Feuilles",
          mediaType: "Toile hollandaise sergée pour feuilles",
          aperture: "5 µm – 25 µm",
          alloy: "AISI 316L",
          cleaning: "Rétrolavage solvant / soufflage vapeur",
        },
        {
          application: "Tamisage Laitier et Boissons",
          mediaType: "Cylindre / panier en toile tissée sanitaire",
          aperture: "50 µm – 250 µm",
          alloy: "AISI 316L / 304",
          cleaning: "Buse NEP automatique / cycle acide",
        },
        {
          application: "Tamisage de Céréales, Farines et Fécules",
          mediaType: "Toile tissée à maille carrée haute tension",
          aperture: "150 µm – 500 µm",
          alloy: "AISI 304 / 316L",
          cleaning: "Décolmatage vibrant / purge d'air",
        },
      ],
      caption: "Toutes les dimensions, disques filtrants et paniers cylindriques sont fabriqués sur mesure selon les plans des équipements.",
    },
    features: {
      heading: "Atouts Fonctionnels et de Fabrication",
      intro: "Nos toiles inox pour l'agroalimentaire allient pureté métallurgique, absence de relargage et robustesse mécanique.",
      items: [
        {
          title: "Tissages Hygiéniques sans Recoins",
          text: "Points d'entrecroisement réguliers et surfaces lisses évitant le piégeage de particules et les biofilms microbiens.",
        },
        {
          title: "Zéro Migration de Particules",
          text: "Structures tout métal rigides et couches frittées éliminant tout relargage de fibres dans les liquides de process.",
        },
        {
          title: "Aptitude aux Cycles NEP et SEP",
          text: "Aciers inoxydables passivés supportant l'action répétée des détergents alcalins, de l'acide nitrique et de la vapeur.",
        },
        {
          title: "Dégraissage aux Ultrasons",
          text: "Nettoyage approfondi par ultrasons éliminant les lubrifiants d'étirage avant le contrôle et l'expédition.",
        },
      ],
    },
    related: {
      heading: "Applications Hygiéniques et Produits Recommandés",
      intro: "Découvrez nos solutions associées pour l'agroalimentaire et la pharmacie ainsi que nos toiles de séparation de précision.",
      cards: [
        {
          title: "Filtration Agroalimentaire et Pharmaceutique",
          text: "Solutions globales de toiles inox hygiéniques pour clarification de fluides, tamisage de poudres et pureté des procédés.",
          to: "/FoodAndPharmaceuticalFiltrationPage",
          badge: "Application Principale",
          linkLabel: "Voir la Présentation",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-photograph-of-a-polished-stainless-70af93.png",
            alt: "Toile métallique hygiénique en acier inoxydable haute précision et disques filtrants pour procédés agroalimentaires et pharmaceutiques",
          },
        },
        {
          title: "Filtration Pharmaceutique",
          text: "Médias frittés et toiles tissées de haute stabilité pour principes actifs, solvants et fluides stériles.",
          to: "/PharmaceuticalFiltrationPage",
          badge: "Application Associée",
          linkLabel: "Explorer la Pharmacie",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-pharmaceuti-9d7134.png",
            alt: "Élément filtrant multicouche en acier inoxydable fritté de précision pour procédés pharmaceutiques",
          },
        },
        {
          title: "Toiles Métalliques Tissées",
          text: "Toiles carrées et toiles hollandaises en acier inoxydable à ouvertures étalonnées pour la séparation de liquides et poudres.",
          to: "/WovenWireMeshPage",
          badge: "Produit Recommandé",
          linkLabel: "Explorer la Toile Tissée",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Toile métallique inox tissage hollandais uni pour clarification sanitaire de liquides",
          },
        },
        {
          title: "Composants Filtrants",
          text: "Paniers sanitaires, cylindres soudés, disques découpés et packs de toiles pour lignes agroalimentaires.",
          to: "/FilterComponentsPage",
          badge: "Produit Recommandé",
          linkLabel: "Explorer les Composants",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/pharma_sterile_gas_filter.png",
            alt: "Cartouche filtrante sanitaire tout métal pour vapeur et fluides",
          },
        },
      ],
    },
    cta: {
      id: "food-processing-quote",
      eyebrow: "Étude Technique",
      heading: "Demandez un Devis pour Toiles Filtrantes Agroalimentaires",
      lead: "Indiquez votre seuil de filtration, les dimensions de vos équipements, la nuance d'acier et vos procédures de nettoyage pour une proposition sur mesure.",
      secondaryLabel: "Retour à l'Agroalimentaire",
      secondaryTo: "/FoodAndPharmaceuticalFiltrationPage",
      note: "Certificats matière (MTR) et contrôles PMI disponibles sur demande selon spécifications de la commande.",
    },
  },
  pt: {
    breadcrumbKey: "FoodProcessingFiltration",
    relatedTopic: "food",
    hero: {
      categoryBadge: "Filtração de Alimentos e Farmacêutica",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Malha de Arame de Aço Inoxidável para Processamento de Alimentos",
      lead: "Projetada em malha tecida de aço inoxidável AISI 316L e 304 para suportar lavagens frequentes CIP/SIP, sanitização a vapor e separação contínua de sólidos e líquidos sem desprendimento de partículas.",
      secondaryCta: {
        label: "Filtração de Alimentos e Farmacêutica",
        to: "/FoodAndPharmaceuticalFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sanitary-st-8ec1d3.png",
        alt: "Cesto de filtro de malha de aço inoxidável sanitário em linha de processamento de alimentos",
      },
      cardNote: "Soldas sanitárias sem frestas e desengraxe ultrassônico",
      cardLink: {
        label: "Consulta Técnica",
        to: "/ContactPage",
      },
      specs: [
        { label: "Faixa de Abertura", value: "5 µm – 500 µm" },
        { label: "Graus de Liga", value: "AISI 316L / 304" },
        { label: "Acabamento Superficial", value: "Limpeza Ultrassônica / Passivado" },
        { label: "Protocolo Sanitário", value: "Compatível CIP / SIP a Vapor" },
      ],
    },
    overview: {
      eyebrow: "Engenharia de Processos",
      heading: "Separação Higiênica Sólido-Líquido e Classificação em Linhas de Alimentos",
      paragraphs: [
        "Na moderna produção de alimentos e bebidas, os meios filtrantes devem garantir retenção confiável e repetível enquanto mantêm padrões sanitários rigorosos ao longo de campanhas produtivas estendidas. Fluidos como xaropes de açúcar, óleos comestíveis, misturas de laticínios e aromatizantes demandam separação precisa sem retenção de resíduos.",
        "A MutualFilter fornece telas metálicas de separação sanitária produzidas com ligas de alta pureza AISI 316L e 304. Tecelagens lisas de precisão, telas holandesas sarjadas e cestos filtrantes robustos são tecidos sob tolerâncias estreitas, gerando perfis de poros lisos e sem fendas que promovem livre escoamento e evitam colônias bacterianas.",
        "Todos os elementos de tela sanitária passam por desengraxe ultrassônico em múltiplos estágios e passivação química para eliminar lubrificantes residuais e contaminação ferrosa superficial. Essa estabilidade assegura total compatibilidade com lavagens cáusticas CIP, enxágues ácidos e esterilização a vapor SIP sem deslocamento de fios ou migração de meio.",
      ],
      keyPoints: [
        "Tramas higiênicas sem frestas prevenindo acúmulo bacteriano e contaminação cruzada entre lotes",
        "Alta resistência mecânica e a choques hidráulicos sob ciclos repetidos de lavagem e pulsos de pressão",
        "Aberturas precisas de 5 µm a 500 µm para clarificação de xaropes, laticínios e peneiramento de grãos",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sanitary-st-8ec1d3.png",
        alt: "Cesto de filtro de malha de aço inoxidável sanitário em linha de processamento de alimentos",
      },
    },
    specTable: {
      heading: "Configurações de Telas para Processamento de Alimentos e Bebidas",
      intro: "Meios filtrantes metálicos representativos para clarificação de líquidos, retenção em suspensões, peneiramento e segurança de linha.",
      columns: [
        { key: "application", label: "Aplicação em Alimentos", align: "left" },
        { key: "mediaType", label: "Estrutura do Meio Filtrante", align: "left" },
        { key: "aperture", label: "Abertura / Grau Nominal", align: "right" },
        { key: "alloy", label: "Liga Padrão", align: "right" },
        { key: "cleaning", label: "Método de Limpeza", align: "right" },
      ],
      rows: [
        {
          application: "Clarificação de Xaropes e Melaço",
          mediaType: "Trama holandesa lisa / laminado sinterizado",
          aperture: "10 µm – 50 µm",
          alloy: "AISI 316L",
          cleaning: "Enxágue água quente / soda CIP",
        },
        {
          application: "Refino de Óleos Vegetais e Folhas Filtrantes",
          mediaType: "Tela holandesa sarjada para folhas",
          aperture: "5 µm – 25 µm",
          alloy: "AISI 316L",
          cleaning: "Retrolavagem solvente / vapor",
        },
        {
          application: "Filtração de Laticínios e Bebidas",
          mediaType: "Cilindro / cesto de tela tecida sanitária",
          aperture: "50 µm – 250 µm",
          alloy: "AISI 316L / 304",
          cleaning: "Aspersão CIP automática / ciclo ácido",
        },
        {
          application: "Peneiramento de Grãos, Farinhas e Amidos",
          mediaType: "Tela tecida de malha quadrada de alta tensão",
          aperture: "150 µm – 500 µm",
          alloy: "AISI 304 / 316L",
          cleaning: "Limpeza vibratória / purga de ar",
        },
      ],
      caption: "Todas as dimensões de tela, discos estampados e cestos cilíndricos são fabricados sob medida conforme projetos dos clientes.",
    },
    features: {
      heading: "Destaques Construtivos e Operacionais",
      intro: "Nossas telas metálicas para processamento de alimentos combinam pureza metalúrgica, zero desprendimento e durabilidade.",
      items: [
        {
          title: "Tramas Higiênicas sem Frestas",
          text: "Pontos de cruzamento uniformes e superfícies lisas que impedem o acúmulo de partículas e contaminação entre bateladas.",
        },
        {
          title: "Zero Migração de Meio",
          text: "Estruturas totalmente metálicas e camadas sinterizadas que eliminam desprendimento de fibras em fluidos sensíveis.",
        },
        {
          title: "Resistência a Limpezas CIP e SIP",
          text: "Ligas de aço inoxidável passivadas resistentes a ciclos térmicos de soda cáustica, ácido nítrico e vapor pressurizado.",
        },
        {
          title: "Desengraxe Ultrassônico",
          text: "Limpeza ultrassônica profunda em múltiplos estágios para remover óleos de fabricação antes da embalagem final.",
        },
      ],
    },
    related: {
      heading: "Aplicações Sanitárias e Produtos Relacionados",
      intro: "Conheça soluções integradas para alimentos e farmácia juntamente com meios metálicos de separação de alta precisão.",
      cards: [
        {
          title: "Filtração de Alimentos e Farmacêutica",
          text: "Soluções completas em telas sanitárias para clarificação de fluidos, peneiramento de pós e pureza de processo.",
          to: "/FoodAndPharmaceuticalFiltrationPage",
          badge: "Aplicação Principal",
          linkLabel: "Ver Visão Geral",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-photograph-of-a-polished-stainless-70af93.png",
            alt: "Malha higiênica de aço inoxidável de alta precisão e discos filtrantes para processamento de alimentos e farmacêutico",
          },
        },
        {
          title: "Filtração Farmacêutica",
          text: "Laminados sinterizados com porosidade estável e telas tecidas para princípios ativos e fluidos estéreis.",
          to: "/PharmaceuticalFiltrationPage",
          badge: "Aplicação Irmã",
          linkLabel: "Explorar Filtração Farmacêutica",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-pharmaceuti-9d7134.png",
            alt: "Elemento filtrante multicamada em aço inoxidável sinterizado de precisão para processos farmacêuticos",
          },
        },
        {
          title: "Telas de Arame Tecidas",
          text: "Malhas quadradas e telas holandesas em aço inoxidável com aberturas calibradas para clarificação de líquidos e pós.",
          to: "/WovenWireMeshPage",
          badge: "Produto Recomendado",
          linkLabel: "Explorar Telas Tecidas",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Tela de arame inox de trama holandesa lisa para clarificação sanitária de líquidos",
          },
        },
        {
          title: "Componentes Filtrantes",
          text: "Cestos sanitários, cilindros soldados, discos estampados e pacotes de telas para equipamentos de alimentos e bebidas.",
          to: "/FilterComponentsPage",
          badge: "Produto Recomendado",
          linkLabel: "Explorar Componentes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/pharma_sterile_gas_filter.png",
            alt: "Cartucho filtrante sanitário metálico para vapor e líquidos",
          },
        },
      ],
    },
    cta: {
      id: "food-processing-quote",
      eyebrow: "Consulta de Engenharia",
      heading: "Solicite uma Cotação para Telas de Processamento de Alimentos",
      lead: "Informe a retenção micrométrica necessária, dimensões de montagem, liga metálica e rotina de sanitização para análise técnica e proposta.",
      secondaryLabel: "Voltar para Alimentos e Farmácia",
      secondaryTo: "/FoodAndPharmaceuticalFiltrationPage",
      note: "Relatórios de ensaio de material (MTR) e análise PMI disponíveis quando especificados no pedido.",
    },
  },
};
